import { useState, MouseEvent, useEffect, useCallback } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { Button } from 'UIPackage/MainModule/components/Button';
import { IKnmNoticeType } from 'APIPackage/CatalogsModule/interfaces/IKnmNoticeType';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { getCatalogKnmNoticeMethodsAll } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { adminCreateCatalogTypes, adminGetCatalogTypes, adminUpdateCatalogTypes } from '../actions/adminAction';
import styles from './AdminEditForm.module.scss';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';

interface ISelectedKnmNoticeMethod extends Omit<IKnmNoticeType, 'id'> {
  id?: number;
}

type KnmNoticeMethodForm = Omit<IKnmNoticeType, 'id'>;

interface IKnmNoticeMethodEditFormProps {
  onBack: () => void;
}

interface IKnmNoticeMethodEditFormStoreProps {
  knmNoticeMethods: IKnmNoticeType[];
}

const KnmNoticeMethodEditFormWithoutStore = ({
  onBack,
  knmNoticeMethods,
}: IKnmNoticeMethodEditFormProps & IKnmNoticeMethodEditFormStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [selectedKnmNoticeMethod, setSelectedKnmNoticeMethod] = useState<ISelectedKnmNoticeMethod | undefined>();
  const [isNew, setIsNew] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const onRowClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const { id } = event.currentTarget.dataset;
      const knmNoticeMethod = knmNoticeMethods.find(k => k.id.toString() === id);

      setSelectedKnmNoticeMethod(prev => (prev?.id === knmNoticeMethod?.id ? undefined : knmNoticeMethod));
    },
    [knmNoticeMethods],
  );

  useEffect(() => {
    dispatch(adminGetCatalogTypes(CommonCatalogsNamesEnum.KNM_NOTICE_METHODS_ALL));
  }, [dispatch]);

  const onCreate = useCallback(() => {
    setIsNew(true);
    setSelectedKnmNoticeMethod({
      enabled: true,
      code: '',
      name: '',
      weight: 0,
    });
    setIsFormOpen(true);
  }, []);

  const onEdit = useCallback(() => setIsFormOpen(true), []);

  const onCancel = useCallback(() => {
    setIsNew(false);
    setSelectedKnmNoticeMethod(undefined);
    setIsFormOpen(false);
  }, []);

  const onSubmit = useCallback(
    (values: KnmNoticeMethodForm) => {
      if (isNew) {
        dispatch(
          adminCreateCatalogTypes({ data: values, catalogName: CommonCatalogsNamesEnum.KNM_NOTICE_METHODS_ALL }),
        );
      } else {
        const { id } = selectedKnmNoticeMethod!;

        if (id) {
          dispatch(
            adminUpdateCatalogTypes({
              data: { id, ...values },
              catalogName: CommonCatalogsNamesEnum.KNM_NOTICE_METHODS_ALL,
            }),
          );
        }
      }
      onCancel();
    },
    [dispatch, isNew, onCancel, selectedKnmNoticeMethod],
  );

  const renderForm = useCallback(
    ({ handleSubmit }: FormRenderProps<KnmNoticeMethodForm>) => (
      <form onSubmit={handleSubmit}>
        <Row>
          <FieldBlock label="Название" color="black">
            <TextInputField name="name" validate={makeRequired('Поле обязательно')} />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Код" color="black">
            <TextInputField name="code" disabled={!isNew} />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Вес" color="black">
            <TextInputField min={1} max={999999999} type="number" name="weight" />
          </FieldBlock>
        </Row>
        <Row>
          <CheckboxField id="enabled" name="enabled" label="Активен" />
        </Row>
        <ModalActions placement="left" padding="none">
          <Button color="primary" text="Сохранить" id="saveButton" type="submit" variant="contained" />
          <Button color="default" text="Отмена" variant="contained" onClick={onCancel} />
        </ModalActions>
      </form>
    ),
    [isNew, onCancel],
  );

  if (isFormOpen) {
    return (
      <>
        <Form<KnmNoticeMethodForm> onSubmit={onSubmit} initialValues={selectedKnmNoticeMethod} render={renderForm} />
      </>
    );
  }

  return (
    <div>
      <div className={styles.Table}>
        <span className={styles.Header}>Способы уведомления</span>
        {knmNoticeMethods &&
          knmNoticeMethods.map((knmNoticeMethod: IKnmNoticeType) => (
            <button
              key={knmNoticeMethod.id}
              className={selectedKnmNoticeMethod?.id === knmNoticeMethod.id ? styles.DataRowSelected : styles.DataRow}
              data-id={knmNoticeMethod.id}
              onClick={onRowClick}
              type="button"
            >
              {knmNoticeMethod.name}
            </button>
          ))}
      </div>
      <div className={styles.ControlButtons}>
        <Button
          className={styles.Button}
          color="default"
          type="button"
          variant="contained"
          onClick={onBack}
          text="Назад"
        />
        <Button
          className={styles.Button}
          color="primary"
          type="button"
          variant="contained"
          onClick={onCreate}
          id="createButton"
          text="Создать"
        />
        {selectedKnmNoticeMethod && (
          <Button
            className={styles.Button}
            color="default"
            type="button"
            variant="contained"
            onClick={onEdit}
            text="Редактировать"
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IKnmNoticeMethodEditFormStoreProps => ({
  knmNoticeMethods: getCatalogKnmNoticeMethodsAll(state),
});

export const KnmNoticeMethodEditForm = connect(mapStateToProps)(KnmNoticeMethodEditFormWithoutStore);
